import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import useTabMusic from "@/hooks/useTabMusic";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Library, Search, Sparkles, Loader2, X, Handshake, Languages, Swords, ChevronLeft, ChevronRight, Backpack, Store } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import HTMLPageFlip from "react-pageflip";
import { resolveImageUrl } from "@/utils/imageUrl";
import { t } from "@/utils/i18n";
import PveDeckBuilder from "@/components/game/PveDeckBuilder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RARITIES = ["legendary", "epic", "rare", "uncommon", "common"];
const RARITY_COLOR = {
  common: "#94a3b8",
  uncommon: "#22c55e",
  rare: "#3b82f6",
  epic: "#a855f7",
  legendary: "#f59e0b",
};

// HTMLPageFlip requires pages to be React.forwardRef
const Page = React.forwardRef(({ children, number, title, isLeft }, ref) => {
  return (
    <div 
      ref={ref} 
      className={`album-page ${isLeft ? "page-left" : "page-right"}`}
      data-density="hard"
    >
      <div className="flex flex-col h-full justify-between">
        <div>
          {title && <h3 className="album-page-title">{title}</h3>}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {children}
          </div>
        </div>
        <div className="album-page-number">
          - {number} -
        </div>
      </div>
    </div>
  );
});
Page.displayName = "Page";

export default function Collection({ onTradeCard }) {
  const { game, setGame } = useGame();
  const { token, API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Custom Zoom State
  const [selected, setSelected] = useState(null);
  const [selectedRect, setSelectedRect] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [retrieving, setRetrieving] = useState(false);

  useTabMusic('collection');

  // Filter state
  const [search, setSearch] = useState("");
  const [rarityFilter, setRarityFilter] = useState("all");
  const [foilFilter, setFoilFilter] = useState("all"); 
  const [ownedFilter, setOwnedFilter] = useState("all"); 
  const [minAtk, setMinAtk] = useState("");
  const [minDef, setMinDef] = useState("");
  const [maxMana, setMaxMana] = useState("");
  const [showDeckBuilder, setShowDeckBuilder] = useState(false);
  const [showTradeChoices, setShowTradeChoices] = useState(false);

  const bookRef = useRef(null);

  const lang = game?.language || "en";
  const isIt = lang.startsWith("it");

  const load = useCallback(async () => {
    if (!game?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/game/${game.id}/collection`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load collection");
    } finally {
      setLoading(false);
    }
  }, [API, token, game?.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data?.entries) return [];
    const q = search.trim().toLowerCase();
    return data.entries.filter(e => {
      if (q && !(e.name || "").toLowerCase().includes(q)) return false;
      if (rarityFilter !== "all" && e.rarity !== rarityFilter) return false;
      if (foilFilter === "foil" && !e.foil) return false;
      if (foilFilter === "non-foil" && e.foil) return false;
      if (ownedFilter === "owned" && !e.currently_owned) return false;
      if (ownedFilter === "lost" && e.currently_owned) return false;
      if (minAtk !== "" && (e.attack || 0) < Number(minAtk)) return false;
      if (minDef !== "" && (e.defense || 0) < Number(minDef)) return false;
      if (maxMana !== "" && (e.mana_cost || 0) > Number(maxMana)) return false;
      return true;
    });
  }, [data, search, rarityFilter, foilFilter, ownedFilter, minAtk, minDef, maxMana]);

  const resetFilters = () => {
    setSearch("");
    setRarityFilter("all");
    setFoilFilter("all");
    setOwnedFilter("all");
    setMinAtk(""); setMinDef(""); setMaxMana("");
  };

  // Turn pages of the album
  const flipPrev = () => {
    if (bookRef.current) {
      bookRef.current.pageFlip().flipPrev();
    }
  };

  const flipNext = () => {
    if (bookRef.current) {
      bookRef.current.pageFlip().flipNext();
    }
  };

  // Card click triggers zoom-out animation
  const handleCardClick = (card, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setSelected(card);
    setSelectedRect(rect);
    setIsZoomed(true);
    setIsAnimating(true);
    setShowTradeChoices(false);
    
    // Apply centered styles on next render loop
    setTimeout(() => {
      setIsAnimating(false);
    }, 20);
  };

  // Close card triggers unzoom animation back to slot
  const handleClose = () => {
    setIsAnimating(true);
    setShowTradeChoices(false);
    setTimeout(() => {
      setIsZoomed(false);
      setSelected(null);
      setSelectedRect(null);
      setIsAnimating(false);
    }, 400); // 0.4s matches App.css .hero-transition-card
  };

  const handleTranslate = async () => {
    if (!selected || !game?.id) return;
    setTranslating(true);
    const tradeCategory = selected.type === "ally" ? "allies" : selected.type === "ability" ? "abilities" : "objects";
    try {
      const res = await axios.post(
        `${API}/game/${game.id}/card/translate`,
        { card_id: selected.id, card_category: tradeCategory },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
      );
      if (res.data?.translated) {
        toast.success(isIt ? "Carta tradotta" : "Card translated");
        load();
        setSelected((prev) => prev ? { ...prev, name: res.data.card.name, description: res.data.card.description, _translations: res.data.card._translations } : prev);
      } else {
        toast.info(isIt ? "Nessuna traduzione necessaria" : "No translation needed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (isIt ? "Traduzione fallita" : "Translation failed"));
    } finally {
      setTranslating(false);
    }
  };

  const handleSendToInventory = async () => {
    if (!selected || !game?.id || retrieving) return;
    setRetrieving(true);
    try {
      const category = selected.type === "ally" ? "allies" : selected.type === "ability" ? "abilities" : "objects";
      const res = await axios.post(
        `${API}/game/${game.id}/collection/retrieve`,
        { card_id: selected.id, card_category: category },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isIt ? "Carta inviata all'inventario" : "Card sent to inventory");
      setGame(res.data);
      handleClose();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isIt ? "Invio fallito" : "Failed to retrieve card"));
    } finally {
      setRetrieving(false);
    }
  };

  // Paginate cards list into pages of 4 items (2x2 grid)
  const cardsPerPage = 4;
  const pagesList = [];
  for (let i = 0; i < filtered.length; i += cardsPerPage) {
    pagesList.push(filtered.slice(i, i + cardsPerPage));
  }
  
  // Complete page spreads
  if (pagesList.length > 0 && pagesList.length % 2 !== 0) {
    pagesList.push([]);
  }

  // Calculate dynamic dimensions for card zoom
  const getZoomedStyles = () => {
    if (!selectedRect) return {};
    if (isAnimating) {
      return {
        position: 'fixed',
        left: selectedRect.left,
        top: selectedRect.top,
        width: selectedRect.width,
        height: selectedRect.height,
        transform: 'none',
      };
    } else {
      const targetWidth = Math.min(290, window.innerWidth - 32);
      const targetHeight = targetWidth * (7 / 5);
      const centerLeft = (window.innerWidth - targetWidth) / 2;
      const centerTop = Math.max(16, (window.innerHeight - targetHeight - 120) / 2);
      return {
        position: 'fixed',
        left: centerLeft,
        top: centerTop,
        width: targetWidth,
        height: targetHeight,
        transform: 'none',
      };
    }
  };

  if (!game) return null;

  const zoomedStyles = getZoomedStyles();
  const tradeCategory = selected?.type === "ally" ? "allies" : selected?.type === "ability" ? "abilities" : "objects";
  const isEnglish = (lang || "en").startsWith("en");
  const alreadyTranslated = selected && !!(selected._translations && selected._translations[(lang || "en").slice(0, 2)]);
  const canTranslate = selected?.currently_owned && !isEnglish && !alreadyTranslated;

  return (
    <div className="py-3 sm:py-6 px-2 sm:px-4" data-testid="collection-view">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3 sm:mb-4">
          <h2 className="font-heading text-base sm:text-xl text-[#cfb53b] flex items-center gap-2">
            <Library size={18} /> {isIt ? "Libro della Collezione" : "Collection Book"}
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeckBuilder(true)}
              data-testid="collection-deck-button"
              className="h-8 text-xs border-[#cfb53b]/40 text-[#cfb53b] hover:bg-[#cfb53b]/10"
            >
              <Swords size={14} className="mr-1.5" />
              {isIt ? "Deck Battaglia" : "Battle Deck"}
            </Button>
            {data && (
              <div className="flex items-center gap-2 text-[10px] sm:text-xs">
                <span className="bg-green-500/10 text-green-400 px-2 py-1 rounded-full font-heading" data-testid="collection-owned-count">
                  {isIt ? "Possedute" : "Owned"}: {data.owned}
                </span>
                <span className="bg-red-500/10 text-red-400 px-2 py-1 rounded-full font-heading">
                  {isIt ? "Perse" : "Lost"}: {data.lost}
                </span>
                <span className="bg-[#cfb53b]/10 text-[#cfb53b] px-2 py-1 rounded-full font-heading">
                  {isIt ? "Totale" : "Total"}: {data.total}
                </span>
              </div>
            )}
          </div>
        </div>

        {data && data.total > 0 && (
          <div className="grid grid-cols-5 gap-1 mb-3 sm:mb-4">
            {RARITIES.slice().reverse().map(r => {
              const s = data.by_rarity[r] || { total: 0, owned: 0, foil: 0 };
              return (
                <div key={r} className="bg-[#0a0a0c] border rounded px-1.5 py-1 text-center" style={{ borderColor: `${RARITY_COLOR[r]}55` }} data-testid={`rarity-summary-${r}`}>
                  <p className="text-[9px] font-heading uppercase" style={{ color: RARITY_COLOR[r] }}>{t(lang, `rarity_${r}`)}</p>
                  <p className="font-heading text-xs sm:text-sm text-[#e2e8f0]">{s.owned}<span className="text-[#64748b]">/{s.total}</span></p>
                  {s.foil > 0 && <p className="text-[9px] text-[#fef08a]">✨ {s.foil}</p>}
                </div>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-2.5 sm:p-3 mb-3 space-y-2" data-testid="collection-filters">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#64748b]" />
            <Input
              data-testid="collection-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isIt ? "Cerca per nome..." : "Search by name..."}
              className="pl-8 h-8 text-xs bg-[#0a0a0c] border-[#2d3748] focus:border-[#cfb53b]/40"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {["all", ...RARITIES].map(r => (
              <button
                key={r}
                data-testid={`rarity-filter-${r}`}
                onClick={() => setRarityFilter(r)}
                className={`text-[10px] font-heading uppercase px-2 py-0.5 rounded-full transition-colors ${
                  rarityFilter === r
                    ? "bg-[#cfb53b]/25 text-[#cfb53b] border border-[#cfb53b]/50"
                    : "bg-[#0a0a0c] text-[#94a3b8] border border-[#2d3748] hover:border-[#cfb53b]/30"
                }`}
                style={rarityFilter === r && r !== "all" ? { color: RARITY_COLOR[r], borderColor: RARITY_COLOR[r] + "66" } : {}}
              >
                {r === "all" ? (isIt ? "Tutte" : "All") : t(lang, `rarity_${r}`)}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
            <select
              data-testid="foil-filter"
              value={foilFilter}
              onChange={(e) => setFoilFilter(e.target.value)}
              className="bg-[#0a0a0c] border border-[#2d3748] rounded px-2 py-1 text-[11px] text-[#e2e8f0]"
            >
              {React.createElement("option", { value: "all" }, isIt ? "Foil: tutte" : "Foil: all")}
              {React.createElement("option", { value: "foil" }, isIt ? "Solo Foil ✨" : "Foil only ✨")}
              {React.createElement("option", { value: "non-foil" }, isIt ? "No Foil" : "No foil")}
            </select>
            <select
              data-testid="owned-filter"
              value={ownedFilter}
              onChange={(e) => setOwnedFilter(e.target.value)}
              className="bg-[#0a0a0c] border border-[#2d3748] rounded px-2 py-1 text-[11px] text-[#e2e8f0]"
            >
              {React.createElement("option", { value: "all" }, isIt ? "Possesso: tutte" : "Ownership: all")}
              {React.createElement("option", { value: "owned" }, isIt ? "Possedute" : "Owned")}
              {React.createElement("option", { value: "lost" }, isIt ? "Perse" : "Lost")}
            </select>
            <input
              data-testid="filter-min-atk"
              type="number" min={0} placeholder={isIt ? "ATT min" : "Min ATK"}
              value={minAtk} onChange={(e) => setMinAtk(e.target.value)}
              className="bg-[#0a0a0c] border border-[#2d3748] rounded px-2 py-1 text-[11px] text-[#e2e8f0]"
            />
            <input
              data-testid="filter-min-def"
              type="number" min={0} placeholder={isIt ? "DIF min" : "Min DEF"}
              value={minDef} onChange={(e) => setMinDef(e.target.value)}
              className="bg-[#0a0a0c] border border-[#2d3748] rounded px-2 py-1 text-[11px] text-[#e2e8f0]"
            />
            <input
              data-testid="filter-max-mana"
              type="number" min={0} placeholder={isIt ? "Costo max" : "Max mana"}
              value={maxMana} onChange={(e) => setMaxMana(e.target.value)}
              className="bg-[#0a0a0c] border border-[#2d3748] rounded px-2 py-1 text-[11px] text-[#e2e8f0]"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-[#64748b]" data-testid="filter-count">
              {filtered.length} / {data?.total || 0} {isIt ? "risultati" : "results"}
            </span>
            <Button size="sm" variant="ghost" onClick={resetFilters} className="h-6 text-[10px] text-[#94a3b8] hover:text-[#cfb53b]" data-testid="reset-filters">
              {isIt ? "Reset" : "Reset"}
            </Button>
          </div>
        </div>

        {/* ALBUM BOOK VIEW */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-[#cfb53b]" /></div>
        ) : error ? (
          <div className="text-center py-12 text-red-400">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-[#2d3748] rounded-lg">
            <Library size={36} className="mx-auto text-[#2d3748] mb-2" />
            <p className="text-[#94a3b8] text-sm">
              {data?.total === 0
                ? (isIt ? "La tua collezione è vuota. Trova o scambia carte per riempirla!" : "Your collection is empty. Find or trade cards to fill it!")
                : (isIt ? "Nessuna carta corrisponde ai filtri." : "No cards match the filters.")}
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Book Flip Wrapper */}
            <div className="album-book-wrapper overflow-hidden px-1">
              <HTMLPageFlip
                width={330}
                height={470}
                size="stretch"
                minWidth={285}
                maxWidth={420}
                minHeight={400}
                maxHeight={600}
                maxShadowOpacity={0.4}
                showCover={false}
                mobileScrollSupport={true}
                ref={bookRef}
                className="mx-auto"
              >
                {pagesList.map((cards, pageIdx) => {
                  const pageNum = pageIdx + 1;
                  const isLeft = pageNum % 2 !== 0;
                  const pageTitle = isIt ? `Capitolo ${Math.ceil(pageNum / 2)}` : `Chapter ${Math.ceil(pageNum / 2)}`;
                  
                  return (
                    <Page key={pageIdx} number={pageNum} title={pageTitle} isLeft={isLeft}>
                      {cards.map((e) => {
                        const color = RARITY_COLOR[e.rarity] || "#94a3b8";
                        const typeClass = e.type === "ally" ? "card-ally" : e.type === "ability" ? "card-ability" : "card-object";
                        const rarityClass = e.rarity ? `rarity-${e.rarity.toLowerCase()}` : "rarity-common";
                        const hasPlaque = e.type === "ally" || (e.type === "object" && ((e.attack || 0) > 0 || (e.defense || 0) > 0));

                        return (
                          <button
                            key={e.id}
                            id={`collection-card-slot-${e.id}`}
                            onClick={(event) => handleCardClick(e, event)}
                            className="group relative text-left transition-all hover:scale-[1.03]"
                          >
                            <div
                              className={`mtg-card ${typeClass} ${rarityClass} variant-compact ${
                                e.currently_owned ? "" : "grayscale-[65%] opacity-60"
                              }`}
                            >
                              <div className={`mtg-inner-frame ${e.foil ? "card-foil" : ""}`}>
                                <div className="mtg-header-bar">
                                  <span className="mtg-card-title">{e.name}</span>
                                  <div className="mtg-header-right">
                                    {e.level > 1 && (
                                      <span className="mtg-level-badge">Lv{e.level}</span>
                                    )}
                                    {e.mana_cost > 0 && (
                                      <div className="mtg-mana-gem">
                                        <span>{e.mana_cost}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="mtg-art-frame">
                                  {e.image_url ? (
                                    <img
                                      src={resolveImageUrl(e.image_url, BACKEND_URL)}
                                      alt={e.name}
                                      className="mtg-art-image"
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div className="mtg-no-art">
                                      <Sparkles size={14} style={{ color }} />
                                    </div>
                                  )}
                                  {!e.currently_owned && (
                                    <div className="absolute inset-0 bg-black/55 flex items-end justify-center pb-1 z-20">
                                      <span className="text-[7px] font-heading uppercase text-red-300 bg-red-900/60 px-1 rounded border border-red-500/30">
                                        {isIt ? "persa" : "lost"}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {hasPlaque && (
                                  <div className="mtg-stat-plaque">
                                    {e.type === "ally" ? (
                                      <>
                                        <span>{e.attack}</span>/<span>{e.hp}</span>
                                      </>
                                    ) : (
                                      <>
                                        {e.attack > 0 && <span>+{e.attack}</span>}
                                        {e.attack > 0 && e.defense > 0 && <span>/</span>}
                                        {e.defense > 0 && <span>+{e.defense}</span>}
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                      {/* Fill empty page slots */}
                      {cards.length < cardsPerPage && 
                        Array.from({ length: cardsPerPage - cards.length }).map((_, i) => (
                          <div key={`empty-${i}`} className="album-empty-slot">
                            <Sparkles size={16} />
                          </div>
                        ))
                      }
                    </Page>
                  );
                })}
              </HTMLPageFlip>
            </div>

            {/* Book Navigation controls */}
            <div className="flex items-center justify-center gap-10 mt-4">
              <button
                onClick={flipPrev}
                className="album-nav-btn px-4 py-1.5 text-xs font-heading flex items-center gap-1"
              >
                <ChevronLeft size={14} /> {isIt ? "Indietro" : "Prev"}
              </button>
              <button
                onClick={flipNext}
                className="album-nav-btn px-4 py-1.5 text-xs font-heading flex items-center gap-1"
              >
                {isIt ? "Avanti" : "Next"} <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* FLIP HERO ZOOM TRANSITION SCREEN */}
      {isZoomed && selected && (
        <div className="fixed inset-0 z-[200] overflow-hidden pointer-events-none">
          {/* Fading transparent backdrop */}
          <div 
            onClick={handleClose}
            className={`hero-overlay ${!isAnimating ? 'overlay-active' : ''}`}
          />

          {/* Floating animated card */}
          <div 
            className="hero-transition-card"
            style={zoomedStyles}
          >
            {(() => {
              const color = RARITY_COLOR[selected.rarity] || "#94a3b8";
              const typeClass = selected.type === "ally" ? "card-ally" : selected.type === "ability" ? "card-ability" : "card-object";
              const rarityClass = selected.rarity ? `rarity-${selected.rarity.toLowerCase()}` : "rarity-common";
              const hasPlaque = selected.type === "ally" || (selected.type === "object" && ((selected.attack || 0) > 0 || (selected.defense || 0) > 0));
              
              return (
                <div 
                  className={`mtg-card ${typeClass} ${rarityClass} ${selected.foil ? "card-foil" : ""}`}
                  style={{ width: '100%', height: '100%', padding: '6px' }}
                >
                  <div className="mtg-inner-frame" style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div className="mtg-header-bar" style={{ height: '24px', padding: '2px 6px' }}>
                      <span className="mtg-card-title" style={{ fontSize: '11px' }}>{selected.name}</span>
                      <div className="mtg-header-right">
                        {selected.level > 1 && (
                          <span className="mtg-level-badge" style={{ fontSize: '8px' }}>Lv{selected.level}</span>
                        )}
                        {selected.mana_cost > 0 && (
                          <div className="mtg-mana-gem" style={{ width: '14px', height: '14px', fontSize: '9px' }}>
                            <span>{selected.mana_cost}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mtg-art-frame" style={{ flex: 1 }}>
                      {selected.image_url ? (
                        <img 
                          src={resolveImageUrl(selected.image_url, BACKEND_URL)} 
                          alt={selected.name} 
                          className="mtg-art-image" 
                          onError={(e) => { e.target.style.display = 'none'; }} 
                        />
                      ) : (
                        <div className="mtg-no-art">
                          <Sparkles size={24} style={{ color }} />
                        </div>
                      )}
                      {!selected.currently_owned && (
                        <div className="absolute inset-0 bg-black/45 flex items-end justify-center pb-1.5 z-20">
                          <span className="text-[8px] font-heading uppercase text-red-300 bg-red-500/40 px-1.5 py-0.5 rounded-full border border-red-500/50">
                            {isIt ? "persa" : "lost"}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Detailed info rules box - fades in once zoomed */}
                    {!isAnimating && (
                      <div className="mtg-rules-box animate-fadeInUp" style={{ height: '84px', padding: '4px 6px', overflow: 'hidden' }}>
                        <div className="mtg-description" style={{ fontSize: '10.5px' }}>
                          {selected.effect && selected.effect !== "none" && selected.effect_value > 0 && (
                            <div className="mb-0.5 font-heading text-[8.5px] uppercase tracking-wider">
                              {selected.effect === "damage" && <span className="text-red-600">DANNO: {selected.effect_value}</span>}
                              {selected.effect === "heal" && <span className="text-green-600">CURA: {selected.effect_value}</span>}
                              {selected.effect === "buff" && <span className="text-amber-600">POTENZIAMENTO: +{selected.effect_value}</span>}
                              {selected.effect === "debuff" && <span className="text-purple-600">DEBUFF: -{selected.effect_value}</span>}
                            </div>
                          )}
                          <p className="leading-tight">{selected.description}</p>
                        </div>
                      </div>
                    )}

                    {hasPlaque && (
                      <div className="mtg-stat-plaque" style={{ bottom: '4px', right: '4px', fontSize: '8px', padding: '1px 4px' }}>
                        {selected.type === "ally" ? (
                          <>
                            <span>{selected.attack}</span> / <span className="text-emerald-700">{selected.hp}</span>
                          </>
                        ) : (
                          <>
                            {selected.attack > 0 && <span>+{selected.attack}</span>}
                            {selected.attack > 0 && selected.defense > 0 && <span>/</span>}
                            {selected.defense > 0 && <span>+{selected.defense}</span>}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Detailed Zoom Actions panel - fades in once fully expanded */}
          {!isAnimating && (
            <div 
              className="fixed flex flex-col gap-2 z-[210] animate-fadeInUp px-2"
              style={{
                top: `${zoomedStyles.top + zoomedStyles.height + 12}px`,
                left: `${zoomedStyles.left}px`,
                width: `${zoomedStyles.width}px`,
                pointerEvents: 'auto',
              }}
            >
              {selected.lost_at && (
                <p className="text-[9px] text-red-400 bg-red-950/40 border border-red-900/30 px-2 py-1 rounded text-center font-heading leading-tight">
                  ⚠️ {isIt ? "Persa il" : "Lost on"}: {new Date(selected.lost_at).toLocaleDateString()}
                  {selected.lost_reason && ` (${selected.lost_reason})`}
                </p>
              )}
              {selected.currently_owned && (
                <button
                  onClick={handleSendToInventory}
                  disabled={retrieving}
                  className="w-full bg-green-600/10 hover:bg-green-600/20 border border-green-500/30 text-green-400 font-heading text-[11px] rounded-lg py-2 flex items-center justify-center gap-2 transition-all shadow-lg disabled:opacity-60"
                >
                  {retrieving ? <Loader2 size={13} className="animate-spin" /> : <Backpack size={13} />}
                  {t(lang, 'send_to_inventory')}
                </button>
              )}
              {selected.currently_owned && onTradeCard && !showTradeChoices && (
                <button
                  onClick={() => setShowTradeChoices(true)}
                  className="w-full bg-[#cfb53b]/10 hover:bg-[#cfb53b]/20 border border-[#cfb53b]/30 text-[#cfb53b] font-heading text-[11px] rounded-lg py-2 flex items-center justify-center gap-2 transition-all shadow-lg"
                  data-testid="collection-trade-btn"
                >
                  <Handshake size={13} />
                  {isIt ? "Scambia questa carta" : "Trade this card"}
                </button>
              )}
              {selected.currently_owned && onTradeCard && showTradeChoices && (
                <div className="flex flex-col gap-1.5 border border-[#cfb53b]/30 bg-[#1a1a1d] p-2 rounded-lg" data-testid="trade-choices-container">
                  <p className="text-[10px] text-center font-heading text-[#cfb53b] mb-1">
                    {isIt ? "Scegli tipo di scambio:" : "Choose trade type:"}
                  </p>
                  <button
                    onClick={() => { onTradeCard(selected.id, tradeCategory, "direct"); handleClose(); }}
                    className="w-full bg-blue-600/10 hover:bg-blue-600/20 border border-blue-500/30 text-blue-400 font-heading text-[11px] rounded-lg py-1.5 flex items-center justify-center gap-2 transition-all shadow-lg"
                    data-testid="trade-choice-direct"
                  >
                    <Handshake size={13} />
                    {isIt ? "Scambio Diretto" : "Direct Trade"}
                  </button>
                  <button
                    onClick={() => { onTradeCard(selected.id, tradeCategory, "market"); handleClose(); }}
                    className="w-full bg-green-600/10 hover:bg-green-600/20 border border-green-500/30 text-green-400 font-heading text-[11px] rounded-lg py-1.5 flex items-center justify-center gap-2 transition-all shadow-lg"
                    data-testid="trade-choice-market"
                  >
                    <Store size={13} />
                    {isIt ? "Vendita in Vetrina" : "Showcase Sale"}
                  </button>
                  <button
                    onClick={() => setShowTradeChoices(false)}
                    className="text-[10px] font-heading text-[#94a3b8] hover:text-white mt-1 text-center"
                    data-testid="trade-choice-cancel"
                  >
                    {isIt ? "Indietro" : "Back"}
                  </button>
                </div>
              )}
              {canTranslate && (
                <button
                  onClick={handleTranslate}
                  disabled={translating}
                  className="w-full bg-[#0a0a0c] hover:bg-[#1a1a1d] border border-[#cfb53b]/30 hover:border-[#cfb53b]/60 text-[#cfb53b] font-heading text-[11px] rounded-lg py-2 flex items-center justify-center gap-2 transition-all disabled:opacity-60 shadow-lg"
                >
                  {translating ? <Loader2 size={13} className="animate-spin" /> : <Languages size={13} />}
                  {translating
                    ? (isIt ? "Traduzione..." : "Translating...")
                    : (isIt ? "Traduci nella mia lingua" : "Translate to my language")}
                </button>
              )}
              {/* Close/Back Button */}
              <button
                onClick={handleClose}
                className="w-full bg-[#8b0000]/15 hover:bg-[#8b0000]/25 border border-red-500/35 text-red-300 font-heading text-[11px] rounded-lg py-2 flex items-center justify-center gap-1.5 transition-all shadow-lg"
              >
                <X size={13} />
                {isIt ? "Chiudi" : "Close"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Iter 105 — PvE Battle Deck builder */}
      {showDeckBuilder && (
        <PveDeckBuilder onClose={() => setShowDeckBuilder(false)} />
      )}
    </div>
  );
}
